// Reading the game's mail list: who the other party is, sent vs received, unread, the week groups, the search match.
// Pure functions over the shapes in src/api/types.ts (docs/api-phase3.md).
import type { MailList, MailListItem, MailMessage } from "@/api/types";

export type MailTab = "all" | "unread" | "sent";

export interface MailItem {
  id: string;
  item: MailListItem;
  subject: string;
  /** the other party's name ("Valor" for system mail) */
  other: string;
  otherId: number;
  /** you wrote the first letter */
  sent: boolean;
  system: boolean;
  unread: boolean;
  /** last update, ms */
  time: number;
}

export const toMs = (t: number | string | null | undefined): number => {
  const n = typeof t === "string" ? Number(t) : t;
  if (!n || !Number.isFinite(n)) return NaN;
  return n > 1e12 ? n : n * 1000;
};

export function mailItems(list: MailList | undefined, uid: number | null): MailItem[] {
  const rows = list?.output?.newsfeed;
  if (!Array.isArray(rows)) return [];
  const out: MailItem[] = rows.map((m) => {
    const fromId = Number(m.b?.from_id ?? -1);
    const sent = uid !== null && fromId === uid;
    const system = Number(m.b?.no_reply) === 1 || fromId === 0;
    return {
      id: String(m.i),
      item: m,
      subject: (m.s ?? "").trim() || "(no subject)",
      other: sent ? m.tl || "Unknown" : m.b?.from_login || (system ? "Valor" : "Unknown"),
      otherId: sent ? -1 : fromId,
      sent,
      system,
      unread: Number(m.r) === 0,
      time: toMs(m.d),
    };
  });
  out.sort((a, b) => (b.time || 0) - (a.time || 0));
  return out;
}

export const matchesTab = (m: MailItem, tab: MailTab): boolean => (tab === "all" ? true : tab === "unread" ? m.unread : m.sent);

export const matchesSearch = (m: MailItem, q: string): boolean => {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  return m.subject.toLowerCase().includes(s) || m.other.toLowerCase().includes(s);
};

/** "This week" (the last seven days) and "Earlier" (with the month of its newest letter as the aside). */
export function weekGroups(items: MailItem[], now: number): { label: string; aside?: string; items: MailItem[] }[] {
  const week: MailItem[] = [], earlier: MailItem[] = [];
  for (const m of items) (Number.isFinite(m.time) && now - m.time < 7 * 86400e3 ? week : earlier).push(m);
  const groups: { label: string; aside?: string; items: MailItem[] }[] = [];
  if (week.length) groups.push({ label: "This week", items: week });
  if (earlier.length) {
    const newest = earlier.find((m) => Number.isFinite(m.time));
    groups.push({ label: "Earlier", aside: newest ? new Date(newest.time).toLocaleDateString("en-US", { month: "long", timeZone: "UTC" }) : undefined, items: earlier });
  }
  return groups;
}

/** The people you have written with, newest first, for the compose screen's RECENT chips. */
export function recentCorrespondents(items: MailItem[], limit = 6): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const m of items) {
    if (m.system || !m.other || seen.has(m.other)) continue;
    seen.add(m.other);
    out.push(m.other);
    if (out.length >= limit) break;
  }
  return out;
}

/** Hero numbers: unread, conversations, distinct correspondents. */
export function mailStats(items: MailItem[]): { unread: number; conversations: number; people: number } {
  return { unread: items.filter((m) => m.unread).length, conversations: items.length, people: new Set(items.filter((m) => !m.system).map((m) => m.other)).size };
}

/** "17 days later" between two letters (empty under a day). */
export function gapLabel(prevMs: number, nextMs: number): string {
  const d = Math.floor((nextMs - prevMs) / 86400e3);
  if (!Number.isFinite(d) || d < 1) return "";
  return d === 1 ? "a day later" : `${d} days later`;
}

export const isMine = (m: MailMessage, uid: number | null): boolean => uid !== null && Number(m.from_id) === uid;

/** Recipient names typed into the compose box: split on commas / semicolons / newlines, trimmed, de-duplicated. */
export function splitRecipients(raw: string): string[] {
  const out: string[] = [];
  for (const part of raw.split(/[,;\n]+/)) {
    const n = part.trim();
    if (n && !out.some((x) => x.toLowerCase() === n.toLowerCase())) out.push(n);
  }
  return out;
}

export const MAIL_MAX_RECIPIENTS = 10;
export const MAIL_MAX_BODY = 2000;
export const MAIL_MAX_SUBJECT = 80;
